// Library Methods
import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Types
import { IndividualExternalExamType } from "src/services/exams/types";
import { PatientType } from "src/services/patients/types";

// Utils
import {
  getReportEyeText,
  getVisualField,
} from "src/utils/exams/examReportsHelper";
import {
  getLocalDateTimeWithZone,
  calculateYearDifference,
} from "src/utils/date/dateHelper";
import { getDecimalNum } from "src/utils/number/numberHelper";
import {
  formatAsPercentage,
  formatIndexAsPercentage,
  toHumanReadable,
} from "src/utils/exams/legacyExamHelper";

// Constants
import { eyeTypeEnums, stimulusColorEnums } from "src/constants/enums";

type LegacyVFCompactSummaryType = {
  patient: PatientType | undefined;
  clinic: { name?: string; timezone?: string | null } | undefined;
  externalExam: IndividualExternalExamType;
};

type RowType = { title: string; value: string | number; isBlueText?: boolean };

// A section's title + rows, rendered as items of the single shared grid in
// LegacyVFCompactSummary below (not its own independent grid) -- separate
// grids can't share column widths with each other, so every section needs
// to be part of the same one for their columns to line up.
const SummarySection = ({
  title,
  rows,
}: {
  title: string;
  rows: Array<RowType>;
}) => (
  <>
    <Typography
      fontSize="11px"
      fontWeight={600}
      sx={{
        gridColumn: "1 / -1",
        textTransform: "uppercase",
        color: "#FFF",
        backgroundColor: "#000",
        p: "4px",
        borderRadius: "2px",
      }}
    >
      {title}
    </Typography>
    {rows.map((row) => (
      <Typography key={row.title} fontSize="11px" sx={{ whiteSpace: "nowrap" }}>
        <span
          style={{
            fontWeight: 600,
          }}
        >
          {row.title}:
        </span>{" "}
        <span style={{ color: Boolean(row.isBlueText) ? "#2563EB" : "#000" }}>
          {row.value}
        </span>
      </Typography>
    ))}
  </>
);

// A dense stand-in for the full LegacyVFExam report (patient header +
// LegacyVFColumns + LegacyVFDetails) -- same underlying fields, grouped
// into full-width sections, each laying its own rows out in 2 columns
// when there's room (see SummarySection).
const LegacyVFCompactSummary = ({
  patient,
  clinic,
  externalExam,
}: LegacyVFCompactSummaryType) => {
  const { t } = useTranslation();

  const eyeAbbreviation = useMemo(() => {
    const { RIGHT, BOTH } = eyeTypeEnums;

    switch (externalExam?.eye) {
      case BOTH:
        return "OU";
      case RIGHT:
        return "OD";
      default:
        return "OS";
    }
  }, [externalExam?.eye]);

  const patientAge = useMemo(
    () => calculateYearDifference(patient?.dateOfBirth, clinic?.timezone),
    [patient?.dateOfBirth, clinic?.timezone]
  );

  const patientId = useMemo(
    () => (patient?.healthCareId ? patient.healthCareId : t("Blank")),
    [patient?.healthCareId, t]
  );

  const rxText = useCallback(
    (
      sphere: number | null | undefined,
      cylinder: number | null | undefined,
      axis: number | null | undefined
    ) => {
      if (sphere === null && cylinder === null && axis === null) {
        return t("No Prescription Entered");
      }

      const sphereText =
        sphere !== null && sphere !== undefined
          ? getDecimalNum(sphere, 2)
          : "N/A";
      const cylinderText =
        cylinder !== null && cylinder !== undefined
          ? getDecimalNum(cylinder, 2)
          : "N/A";
      const axisText =
        axis !== null && axis !== undefined
          ? `${getDecimalNum(axis, 0)}°`
          : "N/A";

      return `${sphereText} sph | ${cylinderText} cyl | ${axisText} ${t("axis")}`;
    },
    [t]
  );

  const localDateAndTimeWithZone = useMemo(() => {
    const examDate = externalExam?.date ?? externalExam?.data?.completionDate;
    return examDate
      ? getLocalDateTimeWithZone(examDate, clinic?.timezone ?? null)
      : { date: "N/A", time: "N/A" };
  }, [
    externalExam?.date,
    externalExam?.data?.completionDate,
    clinic?.timezone,
  ]);

  const hasDefectAnalysis = useMemo(
    () =>
      Boolean(
        externalExam?.data?.patternDefect ||
          externalExam?.data?.overallDefect ||
          (externalExam?.data?.clusterAnalysis &&
            externalExam?.data?.clusterAnalysis !== "N/A")
      ),
    [
      externalExam?.data?.patternDefect,
      externalExam?.data?.overallDefect,
      externalExam?.data?.clusterAnalysis,
    ]
  );

  const patientRows: Array<RowType> = useMemo(
    () => [
      { title: t("Sex"), value: t(patient?.sex ?? "") },
      { title: t("Age"), value: patientAge ?? "N/A" },
      {
        title: t("Birthdate"),
        value: patient?.dateOfBirth?.slice(0, 10) ?? "N/A",
      },
      { title: t("Healthcare Id"), value: patientId },
      {
        title: t("OD Rx"),
        value: rxText(
          patient?.rightSphere,
          patient?.rightCylinder,
          patient?.rightAxis
        ),
      },
      {
        title: t("OS Rx"),
        value: rxText(
          patient?.leftSphere,
          patient?.leftCylinder,
          patient?.leftAxis
        ),
      },
    ],
    [t, patient, patientAge, patientId, rxText]
  );

  const examRows: Array<RowType> = useMemo(
    () => [
      {
        title: t("Eye"),
        value: getReportEyeText(externalExam?.eye ?? "", t),
        isBlueText: true,
      },
      {
        title: t("Fixation Method"),
        value: t(toHumanReadable(externalExam?.data?.fixationMethod)),
        isBlueText: true,
      },
      {
        title: t("Fixation Loss"),
        value: formatAsPercentage(externalExam?.data?.fixationLoss),
        isBlueText: true,
      },
      {
        title: t("False Positives"),
        value: formatAsPercentage(externalExam?.data?.falsePositive),
        isBlueText: true,
      },
      {
        title: t("False Negatives"),
        value: formatAsPercentage(externalExam?.data?.falseNegative),
        isBlueText: true,
      },
      {
        title: t("Grid Type"),
        value: externalExam?.data?.gridType
          ? getVisualField(externalExam.data.gridType)
          : "N/A",
        isBlueText: true,
      },
      { title: t("Speed Type"), value: t("Normal"), isBlueText: true },
      {
        title: t("Stimulus"),
        value: externalExam?.data?.stimulusSize
          ? `${externalExam.data.stimulusSize}, ${t(stimulusColorEnums.WHITE)}`
          : "N/A",
        isBlueText: true,
      },
      { title: t("Date"), value: localDateAndTimeWithZone.date },
      { title: t("Time_hour"), value: localDateAndTimeWithZone.time },
    ],
    [t, externalExam, localDateAndTimeWithZone]
  );

  const indicesRows: Array<RowType> = useMemo(() => {
    const rows: Array<RowType> = [
      { title: t("MD"), value: externalExam?.data?.meanDeviation ?? "N/A" },
      {
        title: t("PSD"),
        value: externalExam?.data?.patternStandardDeviation ?? "N/A",
      },
    ];

    if (
      externalExam?.data?.visualFieldIndex !== undefined &&
      externalExam?.data?.visualFieldIndex >= 0
    ) {
      rows.push({
        title: t("VFI"),
        value: formatIndexAsPercentage(externalExam.data.visualFieldIndex),
      });
    }

    if (externalExam?.data?.glaucomaHemifieldTest !== "Undefined") {
      rows.push({
        title: t("GHT"),
        value: t(toHumanReadable(externalExam?.data?.glaucomaHemifieldTest)),
      });
    }

    if (externalExam?.data?.foveaDb !== "N/A") {
      rows.push({
        title: t("Fovea DB"),
        value: externalExam?.data?.foveaDb ?? "N/A",
      });
    }

    return rows;
  }, [t, externalExam]);

  const defectAnalysisRows: Array<RowType> = useMemo(() => {
    if (!hasDefectAnalysis) return [];

    const rows: Array<RowType> = [];

    if (externalExam?.data?.patternDefect) {
      rows.push({
        title: t("Pattern Defect"),
        value: externalExam.data.patternDefect.toFixed(1),
      });
    }

    if (externalExam?.data?.overallDefect) {
      rows.push({
        title: t("Overall Defect"),
        value: externalExam.data.overallDefect.toFixed(1),
      });
    }

    if (
      externalExam?.data?.clusterAnalysis &&
      externalExam?.data?.clusterAnalysis !== "N/A"
    ) {
      rows.push({
        title: t("Cluster Analysis"),
        value: externalExam.data.clusterAnalysis,
      });
    }

    return rows;
  }, [t, externalExam, hasDefectAnalysis]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        overflowY: "auto",
        overflowX: "auto",
        p: "8px",
        gap: "8px",
        color: "#000",
        backgroundColor: "#FFF",
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: "8px" }}>
          <Typography
            fontSize="12px"
            fontWeight={600}
            sx={{
              whiteSpace: "nowrap",
              color: "#FFF",
              backgroundColor: "#000",
              p: "0px 4px",
              borderRadius: "2px",
            }}
          >
            {eyeAbbreviation}
          </Typography>
          <Typography
            fontSize="12px"
            fontWeight={600}
            sx={{ whiteSpace: "nowrap" }}
          >
            {patient?.firstName} {patient?.lastName}
          </Typography>
        </Box>
        <Typography fontSize="12px" sx={{ whiteSpace: "nowrap" }}>
          {clinic?.name}
        </Typography>
      </Box>

      <Typography fontSize="12px" sx={{ whiteSpace: "nowrap" }}>
        <span style={{ fontWeight: 600 }}>{t("Machine Source")}:</span>{" "}
        {externalExam?.data?.machineSource ?? "N/A"}
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(2, minmax(max-content, 1fr))",
          columnGap: "8px",
          rowGap: "4px",
          width: "100%",
          minWidth: "max-content",
        }}
      >
        <SummarySection title={t("Patient")} rows={patientRows} />
        <SummarySection title={t("Exam")} rows={examRows} />
        <SummarySection title={t("Indices")} rows={indicesRows} />
        {hasDefectAnalysis && (
          <SummarySection
            title={t("Defect Analysis")}
            rows={defectAnalysisRows}
          />
        )}
      </Box>
    </Box>
  );
};

export default LegacyVFCompactSummary;
